import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { CartApiAdapter } from '../adapters/CartApiAdapter';
import { CartFilters } from '../models/Cart';
import { CartService } from '../services/CartService';

export const useCarts = (filters: CartFilters = {}) => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new CartApiAdapter(httpClient);
  const cartService = new CartService(apiAdapter);

  // 🎯 Configurar ordenamiento por defecto: descendente por fecha de creación
  const defaultFilters: CartFilters = {
    sortBy: 'created_at',
    sortDirection: 'desc',
    ...filters,
  };

  const {
    data,
    isLoading,
    isFetching,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['carts', defaultFilters],
    queryFn: ({ pageParam = 1 }) =>
      cartService.getCartsPaginated({
        ...defaultFilters,
        page: pageParam,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    staleTime: 1 * 60 * 1000, // 1 minuto
    gcTime: 5 * 60 * 1000, // 5 minutos
    placeholderData: (previousData) => previousData,
  });

  const carts = data?.pages.flatMap((page) => page.carts) ?? [];
  const totalFromApi = data?.pages[data.pages.length - 1]?.total ?? 0;

  return {
    carts,
    isLoading,
    isFetching,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    totalCarts: carts.length,
    total: totalFromApi,
  };
};

export const useCartsSimple = (filters: CartFilters = {}) => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new CartApiAdapter(httpClient);
  const cartService = new CartService(apiAdapter);

  // 🎯 Configurar ordenamiento por defecto: descendente por fecha de creación
  const defaultFilters: CartFilters = {
    sortBy: 'created_at',
    sortDirection: 'desc',
    ...filters,
  };

  const {
    data: carts = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['carts-simple', defaultFilters],
    queryFn: () => cartService.getAllCarts(defaultFilters),
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
  });

  return {
    carts,
    isLoading,
    error,
  };
};
